/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { createOperationCall, getCustomDomainUrl, OperationHandler } from 'src/core/services/api/createOperationCall';
import { httpService } from 'src/core/services/api/httpService';
import { OperationStatusSwagger } from 'src/core/services/api/operationSwagger';
import { luisRequestOptionsBuilder } from 'src/core/utils/api/luisRequestOptionsBuilder';
import { urlBuilder } from 'src/core/utils/api/urlBuilder';
import { createService } from 'src/core/utils/createService';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { entitySwaggerToEntityStore } from 'src/luis/core/services/entities/mappers';
import { CompositeEntitySwagger } from 'src/luis/projects/entities/types/swagger/CompositeEntitySwagger';
import { LuisId } from 'src/luis/types/Resource';
import { CompositeEntityStore } from 'src/luis/types/stores/entities/CompositeEntityStore';

type CompositeEntitiesMigrationSwagger = { updatedEntities: string[] };

/**
 * Represents a service to perform CRUD operations for composite entity.
 */
export const getCompositeEntityService = createService(({ context, getEnvironment }) => {
    const { apiUrl, isProduction, webApiUrl } = getEnvironment();
    const apiOptions = luisRequestOptionsBuilder()
        .useDefaultOptions(context.serviceKey, isProduction)
        .build();

    /**
     * Creates a common url builder for this service.
     *
     * @param entityId An optional entity id to add.
     * @returns A url string to call.
     */
    const getUrlBuilder = (entityId: LuisId = '') =>
        urlBuilder()
            .baseUrl(apiUrl)
            .app(context.appId)
            .version(context.versionId)
            .resource('compositeEntities', entityId);

    /**
     * Gets the composite entities.
     */
    const getEntities = async (): Promise<CompositeEntityStore[]> => {
        const requestUrl = getUrlBuilder().build();
        const { data: entities } = await httpService.get<CompositeEntitySwagger[]>(requestUrl, apiOptions);

        return entities.map(entity => <CompositeEntityStore>entitySwaggerToEntityStore(entity));
    };

    /**
     * Gets the composite entity with provided id.
     */
    const getEntity = async ({ id }: Pick<CompositeEntityStore, 'id'>): Promise<CompositeEntityStore> => {
        const requestUrl = getUrlBuilder(id).build();
        const { data: entity } = await httpService.get<CompositeEntitySwagger>(requestUrl, apiOptions);

        return <CompositeEntityStore>entitySwaggerToEntityStore(entity);
    };

    /**
     * Creates a new composite entity.
     *
     * @param name The new composite entity name.
     * @param children The new composite entity children.
     *
     * @returns A new composite entity store.
     */
    const createEntity = async ({ name, children }: Pick<CompositeEntityStore, 'name' | 'children'>): Promise<CompositeEntityStore> => {
        const requestUrl = getUrlBuilder().build();
        const { data: entityId } = await httpService.post<string>(requestUrl, { name, children }, apiOptions);

        return await getEntity({ id: entityId });
    };

    /**
     * Deletes the given composite entity.
     *
     * @param id The id of the composite entity to delete.
     */
    const deleteEntity = async ({ id }: Pick<CompositeEntityStore, 'id'>): Promise<void> => {
        const requestUrl = getUrlBuilder(id).build();
        await httpService.delete(requestUrl, apiOptions);
    };

    /**
     * Updates the given composite entity.
     *
     * @param id The id of the entity to update.
     * @param name Optional: The composite entity new name.
     * @param children Optional: The composite entity new children.
     */
    const updateEntity = async ({
        id,
        name,
        childrenNames
    }: Pick<CompositeEntityStore, 'id'> & Partial<Pick<CompositeEntityStore, 'name'>> & { childrenNames: string[] }): Promise<void> => {
        const requestUrl = getUrlBuilder(id).build();
        await httpService.put(requestUrl, { name, children: childrenNames }, apiOptions);
    };

    /**
     * Creates a new child for given composite entity.
     *
     * @param id The composite entity extractor id.
     * @param childName  The name of the new composite child model.
     *
     * @returns The id of newly added child entity.
     */
    const createChildEntity = async ({ id, childName }: Pick<CompositeEntityStore, 'id'> & { childName: string }): Promise<LuisId> => {
        const requestUrl = getUrlBuilder(id)
            .resource('children')
            .build();
        const { data: entityId } = await httpService.post<string>(requestUrl, { name: childName }, apiOptions);

        return entityId;
    };

    /**
     * Deletes the given child from composite entity.
     *
     * @param id The id of the composite entity to delete child from.
     * @param id The id of the child entity to delete.
     */
    const deleteChildEntity = async ({ id, childId }: Pick<CompositeEntityStore, 'id'> & { childId: LuisId }): Promise<void> => {
        const requestUrl = getUrlBuilder(id)
            .resource('children', `${childId}`)
            .build();
        await httpService.delete(requestUrl, apiOptions);
    };

    const migrate = async ({
        handler,
        versionId,
        compositesNames
    }: {
        versionId: string;
        compositesNames: string[];
        handler: OperationHandler<OperationStatusSwagger>;
    }): Promise<CompositeEntitiesMigrationSwagger | null> => {
        const postUrl = urlBuilder()
            .baseUrl(webApiUrl)
            .resource('compositesMigration')
            .app(context.appId)
            .version(context.versionId)
            .resource('migrate')
            .build();

        const { headers: statusOperationUrlHeader } = await httpService.post<OperationStatusSwagger>(
            postUrl,
            { versionId, compositesNames },
            apiOptions
        );
        const operationUrl = (<Record<string, string>>statusOperationUrlHeader)[LuisConstants.OperationHeaders.LocationHeader];

        try {
            const response = await createOperationCall(operationUrl, apiOptions, handler);
            const getUrl = getCustomDomainUrl((<Record<string, string>>response.headers)[LuisConstants.OperationHeaders.LocationHeader]);

            if (response.data?.status === 'failed') {
                throw new Error(response.data.errorDetails);
            }

            const { data } = await httpService.get<CompositeEntitiesMigrationSwagger>(getUrl, apiOptions);

            return data;
        } catch {
            return null;
        }
    };

    return { createEntity, getEntity, updateEntity, deleteEntity, createChildEntity, deleteChildEntity, getEntities, migrate };
});
